import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../../../services/ApiService';
import { useToast } from '../../../contexts/ToastContext';
import Button from '../../ui/Button';
import type { Contributor } from '../../../types/buildExpansion';
import { ContributorRequestModal } from './ContributorRequestModal';

const waitingCopy = (contributor: Contributor): string => {
  if (contributor.waiting_on === 'shop') {
    return 'Waiting for the shop to accept this credit.';
  }
  if (contributor.waiting_on === 'owner') {
    return 'Waiting for the vehicle owner to accept.';
  }
  if (contributor.waiting_on === 'user') {
    return 'Waiting for them to accept.';
  }
  if (String(contributor.status || '').startsWith('pending')) {
    return 'Waiting for the other party to accept.';
  }
  return '';
};

/**
 * Manage-build contributors: current credits with accept/decline/cancel, plus add form.
 */
export const ContributorsManagePanel: React.FC<{
  vehicleId: number;
  isOpen: boolean;
  onChanged?: () => void;
}> = ({ vehicleId, isOpen, onChanged }) => {
  const { showToast } = useToast();
  const [rows, setRows] = useState<Contributor[]>([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await apiService.getVehicleContributors(vehicleId);
      setRows(list.filter((row) => row.status !== 'declined' && row.status !== 'revoked'));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    if (!isOpen) return;
    void load();
  }, [isOpen, load]);

  const act = async (row: Contributor, action: 'accept' | 'decline' | 'revoke') => {
    setActingId(row.id);
    try {
      await apiService.contributorAction(action, { contributor_id: row.id });
      showToast(
        action === 'accept'
          ? 'Contributor added to the build.'
          : action === 'decline'
            ? 'Request declined.'
            : 'Credit removed.',
        'success'
      );
      await load();
      onChanged?.();
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Could not update this credit.', 'error');
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-white mb-1">On this build</h3>
        <p className="text-xs text-gray-500 mb-3">
          Shops and members you do not own must accept before the credit is public.
        </p>
        {loading ? (
          <p className="text-sm text-gray-400">Loading contributors…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500">No contributors on this build yet.</p>
        ) : (
          <ul className="space-y-2 list-none">
            {rows.map((row) => {
              const pending = String(row.status || '').startsWith('pending');
              const label = row.name || row.username || 'Contributor';
              const acting = actingId === row.id;
              return (
                <li
                  key={row.id}
                  className="rounded-lg border border-white/[0.08] bg-[#0c0c0c]/70 p-3 space-y-2"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">{label}</p>
                      <p className="text-xs text-orange-300/90 mt-0.5">{row.role.replace(/_/g, ' ')}</p>
                      {row.work_description ? (
                        <p className="text-xs text-gray-400 mt-1">{row.work_description}</p>
                      ) : null}
                      {pending ? (
                        <p className="text-xs text-amber-200/90 mt-1">{waitingCopy(row)}</p>
                      ) : (
                        <p className="text-xs text-gray-500 mt-1">Live on the build</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {pending && row.can_respond ? (
                      <>
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          loading={acting}
                          disabled={actingId !== null}
                          onClick={() => void act(row, 'accept')}
                        >
                          Accept
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={actingId !== null}
                          onClick={() => void act(row, 'decline')}
                        >
                          Decline
                        </Button>
                      </>
                    ) : null}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={actingId !== null}
                      onClick={() => void act(row, 'revoke')}
                    >
                      {pending ? 'Cancel request' : 'Remove'}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <ContributorRequestModal
        variant="panel"
        isOpen={isOpen}
        onClose={() => undefined}
        vehicleId={vehicleId}
        onCreated={() => {
          void load();
          onChanged?.();
        }}
      />
    </div>
  );
};

export default ContributorsManagePanel;
